<template>
  <div class="w-full">
    <div ref="googleBtn" />
  </div>
</template>

<script setup>
import { onMounted, ref, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/authStore";

const googleBtn = ref(null);
const auth = useAuthStore();
const router = useRouter();
const isProcessing = ref(false);
let cancelled = false;
let promptDisplayed = false;

const emit = defineEmits(["success", "error"]);

const loadGis = () =>
  new Promise((resolve, reject) => {
    if (window?.google?.accounts?.id) return resolve();

    const existing = document.querySelector("script[data-google-identity]");
    if (existing) {
      existing.addEventListener("load", resolve, { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Failed to load Google Identity Services")),
        { once: true }
      );
      return;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.dataset.googleIdentity = "true";
    script.addEventListener("load", resolve, { once: true });
    script.addEventListener(
      "error",
      () => reject(new Error("Failed to load Google Identity Services")),
      { once: true }
    );
    document.head.appendChild(script);
  });

const handleCredentialResponse = async (response) => {
  const idToken = response?.credential;
  if (!idToken || isProcessing.value) return;
  isProcessing.value = true;
  try {
    await auth.loginWithGoogleIdToken(idToken);
    emit("success", response);
    router.push("/home");
  } catch (error) {
    console.error("Google sign-in failed:", error);
    emit("error", error);
  } finally {
    isProcessing.value = false;
  }
};

const initializeButton = () => {
  const g = window?.google?.accounts?.id;
  const clientId = process.env.VUE_APP_GOOGLE_CLIENT_ID;

  if (!g || !clientId) {
    const error = new Error(
      "Google identity services not ready or client ID missing"
    );
    console.error(error.message);
    emit("error", error);
    return;
  }

  g.initialize({
    client_id: clientId,
    callback: handleCredentialResponse,
    ux_mode: "popup",
    auto_select: false,
    itp_support: true,
  });

  g.renderButton(googleBtn.value, {
    type: "standard",
    theme: "outline",
    size: "large",
    shape: "pill",
    text: "continue_with",
    logo_alignment: "center",
    width: "full",
  });

  if (!promptDisplayed) {
    g.prompt?.();
    promptDisplayed = true;
  }
};

const setupGoogle = async () => {
  try {
    await loadGis();
    if (!cancelled) initializeButton();
  } catch (error) {
    console.error("Failed to initialize Google Sign-In:", error);
    emit("error", error);
  }
};

const destroyGoogle = () => {
  try {
    window?.google?.accounts?.id?.cancel?.();
  } catch (error) {
    console.warn("Failed to cancel Google prompt", error);
  }
};

onMounted(() => {
  setupGoogle();
});

onBeforeUnmount(() => {
  cancelled = true;
  destroyGoogle();
});
</script>
